import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  SphereGeometry,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** Passenger seat points in the open rear compartment. The figure origin is at
 *  the feet, so this height sits the hips on the bench with the head clear of
 *  the canopy. */
export const RAKSHA_SEATS = [
  { x: -0.35, y: 0.38, z: 0.55 },
  { x: 0.35, y: 0.38, z: 0.55 },
];

/**
 * A small three-wheeled taxi, modelled with its nose along local -Z like the
 * rest of the scripted traffic. Each vehicle is a group so hitchhikers can be
 * attached to the rear bench without adding another character renderer.
 */
export function buildRakshaVisuals(level, records) {
  const rakshaRecords = records.filter((record) => record.type === 'raksha');
  if (rakshaRecords.length === 0) return { update() {} };

  const template = buildRakshaTemplate();
  const items = rakshaRecords.map((record) => {
    const group = template.clone(true);
    const marker = group.getObjectByName('raksha-fuel-marker');
    if (marker) marker.material = marker.material.clone();
    group.name = `raksha-${record.index}`;
    group.position.set(record.x, record.y ?? 0.02, record.z);
    group.rotation.y = record.yaw;
    group.scale.setScalar(record.scale ?? 1);
    level.root.add(group);
    record.visual = group;
    return { record, group };
  });

  return {
    update(time = 0) {
      for (const { record, group } of items) {
        group.visible = record.visible !== false;
        group.position.set(record.x, record.y ?? 0.02, record.z);
        group.rotation.y = record.yaw;
        group.rotation.z = record.rakshaLean ?? 0;
        const marker = group.getObjectByName('raksha-fuel-marker');
        if (marker) {
          const low = (record.fuel ?? 1) < (record.rakshaConfig?.lowFuelThreshold ?? 0.22);
          marker.material.color.setHex(
            low && Math.sin(time * 10 + record.index) > 0 ? 0xff3b1f : 0xffb52e,
          );
        }
      }
    },
  };
}

function buildRakshaTemplate() {
  const group = new Group();
  const parts = new ColoredParts();
  const paint = 0xe5ad22;
  const paintLight = 0xf3c846;
  const canopy = 0x263b36;
  const dark = 0x202525;
  const metal = 0x89918d;
  const tire = 0x151616;
  const cloth = 0xe1d5b9;
  const skin = 0x68472f;

  // One front wheel and two rear wheels make the silhouette unmistakable.
  parts.cylinder(0.3, 0.3, 0.18, 12, tire, 0, 0.3, -1.02, [0, 0, Math.PI / 2]);
  parts.cylinder(0.12, 0.12, 0.2, 10, metal, 0, 0.3, -1.02, [0, 0, Math.PI / 2]);
  for (const side of [-1, 1]) {
    parts.cylinder(0.32, 0.32, 0.2, 12, tire, side * 0.69, 0.32, 0.83, [0, 0, Math.PI / 2]);
    parts.cylinder(0.14, 0.14, 0.22, 10, metal, side * 0.69, 0.32, 0.83, [0, 0, Math.PI / 2]);
  }

  // Chassis, front cowl, rear tub and the little bench.
  parts.box(1.32, 0.15, 2.28, dark, 0, 0.49, 0.02);
  parts.box(1.08, 0.57, 0.72, paint, 0, 0.78, -0.78);
  parts.box(0.92, 0.15, 0.35, paintLight, 0, 1.08, -0.9);
  parts.box(1.36, 0.12, 1.36, paint, 0, 0.58, 0.48);
  parts.box(1.38, 0.49, 0.12, paint, 0, 0.85, 1.14);
  for (const side of [-1, 1]) {
    parts.box(0.11, 0.4, 1.35, paint, side * 0.66, 0.81, 0.48);
    parts.box(0.07, 1.13, 0.07, metal, side * 0.62, 1.29, 0.98);
    parts.box(0.07, 1.13, 0.07, metal, side * 0.55, 1.29, -0.66);
    parts.box(0.12, 0.08, 0.23, dark, side * 0.65, 1.28, -0.68);
  }
  parts.box(1.12, 0.17, 0.47, dark, 0, 0.83, 0.56);
  parts.box(1.12, 0.42, 0.12, dark, 0, 1.04, 0.85);
  parts.box(0.52, 0.13, 0.42, dark, 0, 0.83, -0.28);
  parts.box(1.48, 0.13, 2.02, canopy, 0, 1.88, 0.1);
  parts.box(1.5, 0.06, 2.05, paintLight, 0, 1.96, 0.1);

  // Handlebars, front fork, lamps and mudguards.
  parts.cylinder(0.035, 0.035, 0.66, 7, metal, 0, 0.62, -1.02, [0.18, 0, 0]);
  parts.cylinder(0.025, 0.025, 0.76, 7, metal, 0, 1.22, -0.55, [0, 0, Math.PI / 2]);
  parts.box(1.15, 0.09, 0.42, paint, 0, 0.56, -1.0);
  parts.box(1.22, 0.12, 0.12, dark, 0, 0.52, 1.25);

  // Driver, seated behind the windscreen with both hands on the bars.
  parts.box(0.39, 0.5, 0.25, cloth, 0, 1.18, -0.28);
  parts.sphere(0.13, 8, 6, skin, 0, 1.55, -0.3);
  parts.cylinder(0.13, 0.14, 0.08, 8, cloth, 0, 1.65, -0.3);
  for (const side of [-1, 1]) {
    parts.box(0.12, 0.13, 0.42, cloth, side * 0.21, 1.26, -0.43);
    parts.sphere(0.06, 6, 4, skin, side * 0.28, 1.23, -0.59);
  }

  const body = new Mesh(
    parts.merge(),
    new MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.12 }),
  );
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);

  const glass = new Mesh(
    new BoxGeometry(1.06, 0.61, 0.035),
    new MeshStandardMaterial({
      color: 0x8fb4b5,
      transparent: true,
      opacity: 0.42,
      roughness: 0.2,
      metalness: 0.15,
      depthWrite: false,
    }),
  );
  glass.position.set(0, 1.46, -0.68);
  glass.rotation.x = -0.12;
  group.add(glass);

  const lights = new ColoredParts();
  lights.box(0.34, 0.22, 0.05, 0xffe6a4, 0, 0.86, -1.16);
  for (const side of [-1, 1]) {
    lights.box(0.19, 0.12, 0.05, 0xb72c20, side * 0.47, 0.88, 1.22);
  }
  const lampMesh = new Mesh(
    lights.merge(),
    new MeshBasicMaterial({ vertexColors: true, toneMapped: false }),
  );
  group.add(lampMesh);

  const marker = new Mesh(
    new BoxGeometry(0.3, 0.09, 0.16),
    new MeshBasicMaterial({ color: 0xffb52e, toneMapped: false }),
  );
  marker.name = 'raksha-fuel-marker';
  marker.position.set(0, 2.04, 0.45);
  group.add(marker);

  return group;
}

class ColoredParts {
  constructor() {
    this.geometries = [];
  }

  box(width, height, depth, color, x, y, z) {
    const geometry = new BoxGeometry(width, height, depth);
    geometry.translate(x, y, z);
    this.#push(geometry, color);
  }

  cylinder(top, bottom, height, segments, color, x, y, z, rotation = null) {
    const geometry = new CylinderGeometry(top, bottom, height, segments);
    if (rotation) {
      if (rotation[0]) geometry.rotateX(rotation[0]);
      if (rotation[1]) geometry.rotateY(rotation[1]);
      if (rotation[2]) geometry.rotateZ(rotation[2]);
    }
    geometry.translate(x, y, z);
    this.#push(geometry, color);
  }

  sphere(radius, width, height, color, x, y, z) {
    const geometry = new SphereGeometry(radius, width, height);
    geometry.translate(x, y, z);
    this.#push(geometry, color);
  }

  merge() {
    const merged = mergeGeometries(this.geometries, false);
    for (const geometry of this.geometries) geometry.dispose();
    this.geometries.length = 0;
    return merged;
  }

  #push(geometry, color) {
    const flat = geometry.index ? geometry.toNonIndexed() : geometry;
    if (flat !== geometry) geometry.dispose();
    const rgb = new Color(color);
    const colors = new Float32Array(flat.attributes.position.count * 3);
    for (let i = 0; i < flat.attributes.position.count; i += 1) {
      colors[i * 3] = rgb.r;
      colors[i * 3 + 1] = rgb.g;
      colors[i * 3 + 2] = rgb.b;
    }
    flat.setAttribute('color', new Float32BufferAttribute(colors, 3));
    this.geometries.push(flat);
  }
}
